import React, { useState, useRef } from "react"
import readXlsxFile from "read-excel-file"
import BaseApi from "../../api/api"
import { NotificationService } from "../../features/NotificationService/NotificationService"
import { selectWbContent } from "../../app/reducers/contentSlice"
import { useSelector } from "react-redux"
import { StatusInfo } from "../UpdateStoksIcon/StatusInfo/StatusInfo"
import { Modal } from "../../features/Modal/Modal"
import { ObjectViewer } from "../ObjectViewer/ObjectViewer"
import { STATUS } from "../../utils/const"
import { getMessage } from "../../msgs/msgs"
import {
  Box,
  Flex,
  Button,
  SegmentedControl,
  Card,
  Badge,
  TextField,
  Heading,
  Text,
  IconButton,
  ScrollArea,
} from "@radix-ui/themes"
import { Cross1Icon, UploadIcon, PlusIcon } from "@radix-ui/react-icons"
import { Select } from "../Select/Select"

interface ExcelData {
  [key: string]: any[]
}

type PhotoField =
  | "photo1"
  | "photo2"
  | "photo3"
  | "photo4"
  | "photo5"
  | "photo6"
  | "photo7"
  | "photo8"
  | "photo9"
  | "photo10"

const PHOTO_FIELDS: PhotoField[] = [
  "photo1",
  "photo2",
  "photo3",
  "photo4",
  "photo5",
  "photo6",
  "photo7",
  "photo8",
  "photo9",
  "photo10",
]

type PhotoMode = "multi" | "single"

const DELIMITER_PRESETS = [
  { label: "Запятая", value: "," },
  { label: "Точка с запятой", value: ";" },
]

interface SelectedColumns {
  article: string
  [key: string]: string
}

interface CardMediaData {
  article: string | number
  photos: string[]
}

const initialSelectedColumns = (): SelectedColumns => ({
  article: "",
  ...Object.fromEntries(PHOTO_FIELDS.map((f) => [f, ""])),
})

export const UpdateWbMedia = () => {
  const [file, setFile] = useState<File | null>(null)
  const [columns, setColumns] = useState<string[]>([])
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumns>(
    initialSelectedColumns(),
  )
  const excelDataRef = useRef<any>(null)
  const [cardData, setCardData] = useState<CardMediaData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [availableFields, setAvailableFields] = useState<PhotoField[]>([
    ...PHOTO_FIELDS,
  ])
  const [selectedFieldType, setSelectedFieldType] = useState<string>("")
  const [showColumnSelect, setShowColumnSelect] = useState(false)
  const [photoMode, setPhotoMode] = useState<PhotoMode>("multi")
  const [singlePhotoColumn, setSinglePhotoColumn] = useState("")
  const [delimiter, setDelimiter] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const wbContentState = useSelector(selectWbContent)

  const [isModalOpen, setModalOpen] = useState(false)

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setIsLoading(true)

    try {
      const data = await readXlsxFile(selectedFile)

      if (data.length > 0) {
        const headers = data[0] as string[]
        setColumns(headers)

        const excelData: ExcelData = {}
        headers.forEach((header, index) => {
          excelData[header] = data.slice(1).map((row) => row[index])
        })

        excelDataRef.current = excelData
      }
    } catch (error) {
      console.error("Ошибка при чтении файла:", error)
      NotificationService.showNotification({
        message: "Ошибка при чтении файла Excel",
        level: "error",
        success: false,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldTypeSelect = (fieldType: string) => {
    setSelectedFieldType(fieldType)
    setShowColumnSelect(true)
  }

  const handleColumnSelect = (columnName: string) => {
    if (selectedFieldType && columnName) {
      setSelectedColumns((prev) => ({
        ...prev,
        [selectedFieldType]: columnName,
      }))

      setAvailableFields((prev) =>
        prev.filter((field) => field !== selectedFieldType),
      )

      setSelectedFieldType("")
      setShowColumnSelect(false)
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const clearData = () => {
    setFile(null)
    setColumns([])
    setSelectedColumns(initialSelectedColumns())
    setCardData([])
    setAvailableFields([...PHOTO_FIELDS])
    setSelectedFieldType("")
    setShowColumnSelect(false)
    setPhotoMode("multi")
    setSinglePhotoColumn("")
    setDelimiter("")
    excelDataRef.current = null
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const removeFieldMapping = (fieldType: string) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [fieldType]: "",
    }))

    if (!availableFields.includes(fieldType as PhotoField)) {
      setAvailableFields((prev) => [...prev, fieldType as PhotoField])
    }
  }

  const generateCardData = () => {
    if (!selectedColumns.article) {
      NotificationService.showNotification({
        message: "Выберите обязательную колонку 'Артикул вб'",
        level: "error",
        success: false,
      })
      return
    }

    if (photoMode === "single") {
      if (!singlePhotoColumn) {
        NotificationService.showNotification({
          message: "Выберите колонку с фото",
          level: "error",
          success: false,
        })
        return
      }
      if (!delimiter) {
        NotificationService.showNotification({
          message: "Укажите разделитель",
          level: "error",
          success: false,
        })
        return
      }
    } else {
      const hasPhotos = PHOTO_FIELDS.some((f) => selectedColumns[f])
      if (!hasPhotos) {
        NotificationService.showNotification({
          message: "Выберите хотя бы одну колонку с фото",
          level: "error",
          success: false,
        })
        return
      }
    }

    if (!excelDataRef.current) return

    const excelData: ExcelData = excelDataRef.current
    const articleData = excelData[selectedColumns.article] || []

    const generatedData: CardMediaData[] = articleData
      .filter((item) => item !== null && item !== undefined && item !== "")
      .map((article, index) => {
        let photos: string[]

        if (photoMode === "single") {
          const raw = excelData[singlePhotoColumn]?.[index]
          photos = raw
            ? String(raw)
                .split(delimiter)
                .map((s) => s.trim())
                .filter(Boolean)
            : []
        } else {
          photos = PHOTO_FIELDS.map((field) => {
            const col = selectedColumns[field]
            if (!col) return ""
            const val = excelData[col]?.[index]
            return val ? String(val) : ""
          }).filter(Boolean)
        }

        return {
          article: String(article),
          photos,
        }
      })
      .filter((card) => card.photos.length > 0)

    setCardData(generatedData)
  }

  const submitData = async () => {
    if (cardData.length === 0) {
      NotificationService.showNotification({
        message: "Нет данных для отправки",
        level: "error",
        success: false,
      })
      return
    }

    try {
      const response = await BaseApi.updateWbMedia(cardData)
      const data = response?.data
      const success = data?.success || false
      NotificationService.showNotification({
        message:
          data?.message || success
            ? "Обновление медиафайлов запущено"
            : "Произошла ошибка при обновлении медиафайлов",
        level: success ? "success" : "error",
        success,
      })
      clearData()
    } catch (error: any) {
      NotificationService.showNotification({
        message: error?.response?.data?.message || "Ошибка при отправке данных",
        level: "error",
        success: false,
      })
    }
  }

  if (wbContentState.updateMediaStatus === STATUS.IN_PROGRESS) {
    return (
      <Box p="4" maxWidth="700px" mx="auto">
        <StatusInfo status={wbContentState.updateMediaStatus} />
      </Box>
    )
  }

  return (
    <Box p="4" maxWidth="700px" mx="auto">
      <Heading size="5" align="center" mb="5">
        Обновление медиафайлов Wildberries
      </Heading>

      <Flex align="center" justify="center" gap="4" mb="4" wrap="wrap">
        <Text size="2" color="gray">
          Статус последнего обновления:
        </Text>
        <StatusInfo
          status={
            wbContentState.mediaErrors?.length
              ? "SUCCESS_WITH_ERROR"
              : (wbContentState.updateMediaStatus as any)
          }
        />
        {wbContentState.mediaErrors?.length ? (
          <>
            <Button
              variant="outline"
              size="1"
              onClick={() => setModalOpen(true)}
            >
              посмотреть ошибки
            </Button>
            <Modal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              onOk={() => setModalOpen(false)}
              content={
                <ObjectViewer
                  data={wbContentState.mediaErrors}
                  title="Ошибки при обновлении медиафайлов"
                />
              }
              hideCancelBtn
            />
          </>
        ) : null}
      </Flex>

      <Flex direction="column" gap="3" mb="4">
        {!file && (
          <Button onClick={handleAttachClick} disabled={isLoading} size="3">
            <UploadIcon />
            {isLoading ? "Загрузка..." : "Прикрепить Excel"}
          </Button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />

        {file && (
          <Flex
            align="center"
            justify="between"
            p="2"
            style={{
              backgroundColor: "var(--gray-3)",
              borderRadius: "var(--radius-2)",
            }}
          >
            <Text size="2">Файл: {file.name}</Text>
            <IconButton
              variant="ghost"
              color="red"
              size="1"
              onClick={clearData}
            >
              <Cross1Icon />
            </IconButton>
          </Flex>
        )}
      </Flex>

      {columns.length > 0 && (
        <Card size="2" style={{ marginBottom: 16 }}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                Артикул вб <Text color="red">*</Text>
              </Text>
              <Select
                value={selectedColumns.article}
                onChange={(value) =>
                  setSelectedColumns((prev) => ({ ...prev, article: value }))
                }
                options={columns.map((column) => ({
                  value: column,
                  label: column,
                }))}
                placeholder="-- Выберите колонку --"
              />
            </Flex>

            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                Режим загрузки фото:
              </Text>
              <SegmentedControl.Root
                value={photoMode}
                onValueChange={(value) => setPhotoMode(value as PhotoMode)}
              >
                <SegmentedControl.Item value="multi">
                  Разные колонки
                </SegmentedControl.Item>
                <SegmentedControl.Item value="single">
                  Одна колонка
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </Flex>

            {photoMode === "multi" && (
              <>
                {PHOTO_FIELDS.some((f) => selectedColumns[f]) && (
                  <Flex
                    direction="column"
                    gap="2"
                    p="3"
                    style={{
                      backgroundColor: "var(--green-2)",
                      borderRadius: "var(--radius-2)",
                    }}
                  >
                    <Text size="2" weight="medium" color="green">
                      Колонки фото:
                    </Text>
                    <Flex gap="2" wrap="wrap">
                      {PHOTO_FIELDS.map((field) => {
                        if (selectedColumns[field]) {
                          return (
                            <Badge key={field} color="green" size="2">
                              <Flex align="center" gap="1">
                                <Text size="1">
                                  {getMessage(field)}: {selectedColumns[field]}
                                </Text>
                                <IconButton
                                  variant="ghost"
                                  color="red"
                                  size="1"
                                  onClick={() => removeFieldMapping(field)}
                                >
                                  <Cross1Icon />
                                </IconButton>
                              </Flex>
                            </Badge>
                          )
                        }
                        return null
                      })}
                    </Flex>
                  </Flex>
                )}

                {availableFields.length > 0 && !showColumnSelect && (
                  <Flex
                    direction="column"
                    gap="1"
                    p="3"
                    style={{
                      border: "1px dashed var(--gray-6)",
                      borderRadius: "var(--radius-2)",
                    }}
                  >
                    <Text as="label" size="2" weight="medium">
                      Добавить фото:
                    </Text>
                    <Select
                      value={selectedFieldType}
                      onChange={handleFieldTypeSelect}
                      options={availableFields.map((field) => ({
                        value: field,
                        label: getMessage(field),
                      }))}
                      placeholder="-- Выберите позицию фото --"
                    />
                  </Flex>
                )}

                {showColumnSelect && (
                  <Flex
                    direction="column"
                    gap="2"
                    p="3"
                    style={{
                      backgroundColor: "var(--blue-2)",
                      borderRadius: "var(--radius-2)",
                    }}
                  >
                    <Text as="label" size="2" weight="medium" color="blue">
                      Выберите колонку для {getMessage(selectedFieldType)}:
                    </Text>
                    <Select
                      value=""
                      onChange={handleColumnSelect}
                      options={columns.map((column) => ({
                        value: column,
                        label: column,
                      }))}
                      placeholder="-- Выберите колонку --"
                    />
                    <Button
                      variant="outline"
                      color="gray"
                      size="1"
                      onClick={() => {
                        setSelectedFieldType("")
                        setShowColumnSelect(false)
                      }}
                    >
                      Отмена
                    </Button>
                  </Flex>
                )}
              </>
            )}

            {photoMode === "single" && (
              <Flex
                direction="column"
                gap="4"
                p="3"
                style={{
                  backgroundColor: "var(--gray-2)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Flex direction="column" gap="1">
                  <Text as="label" size="2" weight="medium">
                    Колонка с фото <Text color="red">*</Text>
                  </Text>
                  <Select
                    value={singlePhotoColumn}
                    onChange={setSinglePhotoColumn}
                    options={columns.map((column) => ({
                      value: column,
                      label: column,
                    }))}
                    placeholder="-- Выберите колонку --"
                  />
                </Flex>

                <Flex direction="column" gap="2">
                  <Text as="label" size="2" weight="medium">
                    Разделитель:
                  </Text>
                  <Flex gap="1" wrap="wrap">
                    {DELIMITER_PRESETS.map((preset) => (
                      <Button
                        key={preset.value}
                        variant={
                          delimiter === preset.value ? "solid" : "outline"
                        }
                        size="1"
                        onClick={() => setDelimiter(preset.value)}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Flex>
                  <TextField.Root
                    size="3"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                    placeholder="Или введите свой разделитель..."
                  />
                </Flex>
              </Flex>
            )}

            {selectedColumns.article &&
              (photoMode === "multi"
                ? PHOTO_FIELDS.some((f) => selectedColumns[f])
                : singlePhotoColumn && delimiter) && (
                <Button color="green" size="3" onClick={generateCardData}>
                  <PlusIcon />
                  Сформировать данные
                </Button>
              )}
          </Flex>
        </Card>
      )}

      {cardData.length > 0 && (
        <Flex direction="column" gap="4">
          <Card size="2">
            <Flex direction="column" gap="3">
              <Heading size="4">Сформированные данные:</Heading>
              <Text size="2" color="gray">
                Всего карточек: {cardData.length}
              </Text>
              <ScrollArea style={{ maxHeight: 300 }}>
                <Flex direction="column" gap="2">
                  {cardData.slice(0, 5).map((card, index) => (
                    <Card
                      key={index}
                      variant="classic"
                      size="1"
                      style={{ borderLeft: "3px solid var(--accent-9)" }}
                    >
                      <Flex direction="column" gap="1">
                        <Flex gap="2">
                          <Text size="2" weight="bold">
                            Артикул:
                          </Text>
                          <Text size="2">{card.article}</Text>
                        </Flex>
                        <Flex gap="2">
                          <Text size="2" weight="bold">
                            Фото:
                          </Text>
                          <Text size="2">{card.photos.length} шт.</Text>
                        </Flex>
                        {card.photos.slice(0, 2).map((url, i) => (
                          <Flex key={i} gap="2">
                            <Text size="2" weight="bold">
                              Фото {i + 1}:
                            </Text>
                            <Text size="2" style={{ wordBreak: "break-all" }}>
                              {url}
                            </Text>
                          </Flex>
                        ))}
                        {card.photos.length > 2 && (
                          <Text size="1" color="gray">
                            ... и ещё {card.photos.length - 2} фото
                          </Text>
                        )}
                      </Flex>
                    </Card>
                  ))}
                  {cardData.length > 5 && (
                    <Text
                      size="2"
                      color="gray"
                      align="center"
                      style={{ fontStyle: "italic" }}
                    >
                      ... и еще {cardData.length - 5} карточек
                    </Text>
                  )}
                </Flex>
              </ScrollArea>
            </Flex>
          </Card>

          <Button size="3" onClick={submitData}>
            Запустить обновление
          </Button>
        </Flex>
      )}
    </Box>
  )
}
